import { useContext, useState, useEffect, useRef } from "react";

import { AccessibilityNew } from "@mui/icons-material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import KeyIcon from "@mui/icons-material/Key";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { Box, IconButton } from "@mui/material";
import { SubmissionSummary } from "@rubi/api-generated";
import { colors, toast, useMapColorScheme } from "@rubi/ui";
import {
  AdvancedMarker,
  Map,
  MapControl,
  Marker,
  useMap,
} from "@vis.gl/react-google-maps";

import { OnlineReportingContext } from "@/components/online-reporting/context";
import { getLocationsFromSubmission } from "@/components/online-reporting/utils";

export interface FocusedPlace {
  lat: number;
  lng: number;
  label: string;
}

export interface FocusedEntry {
  id: string;
  token: number;
}

interface RegistryMapViewProps {
  reports: SubmissionSummary[];
  loadingEntries: boolean;
  onPinClick?: (submission: SubmissionSummary) => void;
  selectedReportId?: string;
  mapCenter?: google.maps.LatLngLiteral;
  focusedPlace?: FocusedPlace | null;
  focusedEntry?: FocusedEntry | null;
  onViewportChange?: (bounds: google.maps.LatLngBoundsLiteral) => void;
  focusResultsToken?: number;
}

interface Location {
  lat: number;
  lng: number;
  submission_id: string;
  submissionData?: SubmissionSummary;
  type?: "camera" | "location" | "key" | "bicycle" | "vulnerable";
}

// Registry-type-specific pin glyphs. These take precedence; any type not listed
// here (including the generic "location" type) falls back to the default empty
// pin glyph.
const PIN_ICON_BY_TYPE: Record<string, typeof CameraAltIcon> = {
  camera: CameraAltIcon,
  key: KeyIcon,
  bicycle: DirectionsBikeIcon,
  vulnerable: AccessibilityNew,
};

const PLACE_FOCUS_ZOOM = 16;
const SELECTED_PIN_Z_INDEX = 1000;
const ENTRY_FOCUS_ZOOM = 18;
const RESULTS_FIT_PADDING = 64;
const MIN_RESULTS_FIT_SPAN = 0.005;
const MIN_ENTRY_FIT_SPAN = 0.001;

const fitMapToLocations = (
  map: google.maps.Map,
  points: Location[],
  minSpan = MIN_RESULTS_FIT_SPAN,
) => {
  const bounds = new google.maps.LatLngBounds();
  points.forEach(({ lat, lng }) => bounds.extend({ lat, lng }));

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latPad = Math.max(0, (minSpan - (ne.lat() - sw.lat())) / 2);
  const lngPad = Math.max(0, (minSpan - (ne.lng() - sw.lng())) / 2);
  if (latPad || lngPad) {
    bounds.extend({ lat: ne.lat() + latPad, lng: ne.lng() + lngPad });
    bounds.extend({ lat: sw.lat() - latPad, lng: sw.lng() - lngPad });
  }

  map.fitBounds(bounds, RESULTS_FIT_PADDING);
};

const focusMapOnEntry = (map: google.maps.Map, points: Location[]) => {
  if (points.length === 1) {
    map.panTo({ lat: points[0].lat, lng: points[0].lng });
    map.setZoom(Math.max(map.getZoom() ?? 0, ENTRY_FOCUS_ZOOM));
    return;
  }
  fitMapToLocations(map, points, MIN_ENTRY_FIT_SPAN);
};

const DEFAULT_MAP_CENTER: google.maps.LatLngLiteral = {
  lat: 37.0902,
  lng: -95.7129,
};

const RegistryMapView = ({
  reports,
  loadingEntries,
  onPinClick,
  selectedReportId,
  mapCenter,
  focusedPlace,
  focusedEntry,
  onViewportChange,
  focusResultsToken,
}: RegistryMapViewProps) => {
  const mapColorScheme = useMapColorScheme();
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [usersLocation, setUsersLocation] =
    useState<google.maps.LatLngLiteral | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const pendingResultsFitRef = useRef(false);
  const appliedEntryFocusRef = useRef<number>();
  const map = useMap();
  const googleMapId = "4380cd85df35e961";

  const { getSubmissionForID } = useContext(OnlineReportingContext);

  const getCurrentPosition = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUsersLocation({ lat: latitude, lng: longitude });
        map?.setCenter({ lat: latitude, lng: longitude });
      },
      (error) => {
        toast.error("Unable to get location: " + error.message);
      },
    );
  };

  const panToCurrentLocation = () => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted" || result.state === "prompt") {
          getCurrentPosition();
        } else {
          toast.error("Location access denied");
        }
      });
    } else if (navigator.geolocation) {
      getCurrentPosition();
    }
  };

  // const findGoogleMapPlaces = (obj: any, submission_id: string) => {
  //   const newLocations: Location[] = [];

  //   const searchObject = (obj: any) => {
  //     if (!obj || typeof obj !== "object") return;

  //     if (
  //       obj.googleMapsPlace &&
  //       obj.googleMapsPlace.geometry &&
  //       obj.googleMapsPlace.geometry
  //     ) {
  //       newLocations.push({
  //         lat: obj.googleMapsPlace.geometry.lat,
  //         lng: obj.googleMapsPlace.geometry.lng,
  //         submission_id: submission_id,
  //       });
  //     }

  //     Object.values(obj).forEach((value) => {
  //       if (value && typeof value === "object") {
  //         searchObject(value);
  //       }
  //     });
  //   };

  //   searchObject(obj);
  //   return newLocations;
  // };

  // Must stay above the loader below: effects run in source order, and the
  // loader is what applies the pending fit.
  useEffect(() => {
    if (focusResultsToken === undefined) return;
    pendingResultsFitRef.current = true;
  }, [focusResultsToken]);

  useEffect(() => {
    const loadLocationsWithSubmissionData = async () => {
      const allLocations: Location[] = [];
      for (const report of reports) {
        const reportLocations = getLocationsFromSubmission(
          { submission: report.latest_data! },
          report.survey_name ?? "",
        ).map((location) => {
          let type = "location";
          if (report.survey_class?.toLowerCase().includes("keyholder")) {
            type = "key";
          } else if (report.survey_class?.toLowerCase().includes("bicycle")) {
            type = "bicycle";
          } else if (
            report.survey_class?.toLowerCase().includes("vulnerable")
          ) {
            type = "vulnerable";
          }
          return {
            lat: location.googleMapsPlace.geometry.lat,
            lng: location.googleMapsPlace.geometry.lng,
            submission_id: report.id,
            type,
          };
        });

        if (report.survey_class?.toLowerCase().includes("camera")) {
          if (report.latest_data?.["location-registered_address"]) {
            const cameras =
              report.latest_data?.["location-registered_address"]?.[
                "cameraLocations"
              ];
            cameras?.forEach((camera) => {
              reportLocations.push({
                lat: camera.lat,
                lng: camera.lng,
                submission_id: report.id,
                type: "camera",
              });
            });
          }
        }
        for (const location of reportLocations) {
          location.submissionData = report;
          location.submission_id = report.id;
          allLocations.push(location);
        }
      }
      setLocations(allLocations);

      if (pendingResultsFitRef.current) {
        pendingResultsFitRef.current = false;
        if (map && allLocations.length > 0) {
          fitMapToLocations(map, allLocations);
        }
      }
    };
    setLocations([]);
    if (loadingEntries) return;
    loadLocationsWithSubmissionData();
  }, [reports]);

  useEffect(() => {
    if (!map || !focusedPlace) return;
    map.panTo({ lat: focusedPlace.lat, lng: focusedPlace.lng });
    map.setZoom(PLACE_FOCUS_ZOOM);
  }, [map, focusedPlace]);

  useEffect(() => {
    if (!map || !focusedEntry) return;
    if (appliedEntryFocusRef.current === focusedEntry.token) return;
    const points = locations.filter(
      (location) => location.submission_id === focusedEntry.id,
    );
    if (points.length === 0) return;
    appliedEntryFocusRef.current = focusedEntry.token;
    focusMapOnEntry(map, points);
  }, [map, focusedEntry, locations]);

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <Map
        colorScheme={mapColorScheme}
        onTilesLoaded={() => setIsMapLoaded(true)}
        onIdle={(event) => {
          const bounds = event.map.getBounds();
          if (bounds) onViewportChange?.(bounds.toJSON());
        }}
        defaultCenter={mapCenter ?? DEFAULT_MAP_CENTER}
        defaultZoom={13}
        gestureHandling="greedy"
        disableDefaultUI={true}
        mapId={googleMapId}
      >
        {isMapLoaded &&
          locations.map((location, index) => {
            // Registry-type-specific icon takes precedence; unidentified types
            // fall back to a plain pin with no icon. The marker is rendered as
            // custom HTML rather than via <Pin> because the modern Google Maps
            // PinElement (API >= 3.62) ignores React-children glyphs, so the
            // icons never appeared.
            const PinIcon = location.type
              ? PIN_ICON_BY_TYPE[location.type]
              : undefined;
            const isSelected = location.submission_id === selectedReportId;
            const pinColor = isSelected
              ? colors.primary.light
              : location.submissionData?.private_registration
                ? colors.success.light
                : colors.legacy.coral;

            return (
              <AdvancedMarker
                key={location.submission_id + index}
                zIndex={isSelected ? SELECTED_PIN_Z_INDEX : undefined}
                position={{
                  lat: location.lat,
                  lng: location.lng,
                }}
                onClick={() => {
                  const submission = reports.find(
                    (r) => r.id === location.submission_id,
                  );
                  if (submission && onPinClick) {
                    onPinClick(submission);
                  }
                }}
              >
                <Box
                  sx={{ position: "relative", width: "28px", height: "36px" }}
                >
                  {/* Pin head */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      boxSizing: "border-box",
                      bgcolor: pinColor,
                      border: "2px solid var(--primary-contrast-text)",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {PinIcon ? (
                      <PinIcon
                        sx={{
                          fontSize: "16px",
                          color: colors.primary.contrastText,
                        }}
                      />
                    ) : (
                      // Fallback "empty pin" glyph for unidentified registry types
                      <Box
                        sx={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          bgcolor: colors.primary.contrastText,
                        }}
                      />
                    )}
                  </Box>
                  {/* Pin pointer (tip sits at bottom-center, i.e. the coordinate) */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 0,
                      height: 0,
                      borderLeft: "6px solid transparent",
                      borderRight: "6px solid transparent",
                      borderTop: `10px solid ${pinColor}`,
                    }}
                  />
                </Box>
              </AdvancedMarker>
            );
          })}

        {isMapLoaded && (
          <MapControl position={google.maps.ControlPosition.RIGHT_BOTTOM}>
            <Box
              sx={{
                bgcolor: "background.paper",
                boxShadow: 2,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                margin: "10px",
              }}
            >
              <IconButton
                onClick={panToCurrentLocation}
                aria-label="Pan to current location"
              >
                <MyLocationIcon />
              </IconButton>
            </Box>
          </MapControl>
        )}

        {isMapLoaded && focusedPlace && (
          <AdvancedMarker
            position={{ lat: focusedPlace.lat, lng: focusedPlace.lng }}
            title={focusedPlace.label}
          >
            <Box
              sx={{
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                bgcolor: "#1a73e8",
                border: "3px solid #FFFFFF",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.5)",
              }}
            />
          </AdvancedMarker>
        )}

        {isMapLoaded && usersLocation && (
          <Marker
            position={usersLocation}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: colors.primary.main,
              fillOpacity: 1,
              strokeColor: colors.primary.contrastText,
              strokeWeight: 2,
            }}
          />
        )}
      </Map>
    </div>
  );
};

export default RegistryMapView;
